import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { BillEntryMethod } from 'common/src/types/enums/BillEntryMethod.enum';

import { dismissAllocationReview } from 'portal/src/api/BillsService';
import { QUERY_KEYS } from 'portal/src/api/constants';
import ConfirmDialog from 'portal/src/components/common/ConfirmDialog';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

const determineFormTitle = (
  entryMethod: BillEntryMethod,
): { title: string; tooltip: string } => {
  switch (entryMethod) {
    case BillEntryMethod.Allocated:
      return {
        title: 'Allocated Bill Information',
        tooltip: 'This bill was allocated according to an allocation template.',
      };
    case BillEntryMethod.Automatic:
      return {
        title: 'Automated Bill Information',
        tooltip:
          "This bill was created through the platform's bill automation process.",
      };
    case BillEntryMethod.Manual:
      return {
        title: 'Bill Information',
        tooltip: 'This bill was entered manually through the platform.',
      };
    case BillEntryMethod.Imported:
      return {
        title: 'Imported Bill Information',
        tooltip: 'This bill was imported in a bulk upload.',
      };
    case BillEntryMethod.Transferred:
      return {
        title: 'Transferred Bill Information',
        tooltip:
          'This bill was ingested through another software system and ported over to the platform.',
      };
    default:
      return { title: 'Bill Information', tooltip: '' };
  }
};

const buttonStyles = {
  fontSize: 17,
  fontWeight: 'medium',
};

const BillFormHeader = ({
  billId,
  billEntryMethod,
  isPaid,
  showWarningBadge,
}: {
  billId?: string;
  billEntryMethod?: BillEntryMethod;
  isPaid?: boolean;
  showWarningBadge?: boolean;
}) => {
  const snackbarHandler = useSnackbarHandler();
  const client = useQueryClient();
  const { title, tooltip } = determineFormTitle(billEntryMethod);

  const [showDismissDialog, setShowDismissDialog] = useState(false);

  const { mutate: dismissAllocationWarning, isPending } = useMutation({
    mutationFn: async () =>
      dismissAllocationReview(billId, { allocationsEdited: true }),
    onSuccess: () => {
      setShowDismissDialog(false);
      client.invalidateQueries({ queryKey: [QUERY_KEYS.detailPageData] });
    },
    onError: () => snackbarHandler.error('Unable to dismiss warning.'),
  });

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      justifyContent="space-between"
      width="100%"
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="h6">{title}</Typography>
        {tooltip && (
          <Tooltip title={tooltip}>
            <InfoOutlineIcon fontSize="small" />
          </Tooltip>
        )}
      </Box>
      <Stack direction="column" spacing={1} alignItems="flex-end">
        {isPaid && (
          <Chip
            color="success"
            icon={<CheckCircleOutlineOutlinedIcon />}
            label="PAID"
            variant="outlined"
            size="small"
            sx={buttonStyles}
          />
        )}
        {showWarningBadge && (
          <Chip
            color="warning"
            icon={<InfoOutlineIcon />}
            label={
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="body1" sx={{ ...buttonStyles }}>
                  Review Allocation Line Items
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setShowDismissDialog(true)}
                  sx={{ padding: 0 }}
                >
                  <CancelIcon fontSize="small" color="warning" />
                </IconButton>
              </Box>
            }
            size="small"
            variant="outlined"
          />
        )}
      </Stack>
      <ConfirmDialog
        open={showDismissDialog}
        onClose={() => setShowDismissDialog(false)}
        message={
          <Typography>
            This bill was allocated using estimated monthly percentages.
            <br />
            By dismissing this warning, you are confirming the bill's line items
            are accurate.
          </Typography>
        }
        primaryButtonLabel="Yes, Dismiss"
        onPrimaryButtonClicked={dismissAllocationWarning}
        secondaryButtonLabel="Cancel"
        onSecondaryButtonClicked={() => setShowDismissDialog(false)}
        renderElement={<WarningIcon sx={{ color: 'warning.main' }} />}
        isLoading={isPending}
      />
    </Box>
  );
};

export default BillFormHeader;
